import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { searchUsers, AdminUser } from '../adminApi';

type SortableColumn = 'email' | 'created_at' | 'last_active_at' | 'membership_status';
type SortOrder = 'asc' | 'desc';

// Membership status options match the values written by the freemium pipeline.
// Empty string in the value position = "no filter" — Select needs a non-undefined
// value for controlled mode.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_migration', label: 'Pending Migration' },
  { value: 'onboarding', label: 'Onboarding' },
];

// Activity window options. -1 is the sentinel for "inactive >30 days OR never
// active" so the backend can distinguish it from "no filter" (null).
const ACTIVITY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: '7', label: 'Active in 7d' },
  { value: '30', label: 'Active in 30d' },
  { value: '-1', label: 'Inactive (>30d / never)' },
];

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortableColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchUsers({
        query: searchQuery || undefined,
        status: statusFilter || undefined,
        active_within_days: activityFilter === '' ? undefined : parseInt(activityFilter, 10),
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page + 1,
        limit: rowsPerPage,
      });
      setUsers(result.users || []);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, activityFilter, sortBy, sortOrder, page, rowsPerPage]);

  // Clicking a sortable header toggles direction if it's the active column,
  // or switches to that column with a sensible default direction (asc for
  // text columns, desc for date columns).
  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder(column === 'email' || column === 'membership_status' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getMembershipChip = (user: AdminUser) => {
    if (!user.membership_status) {
      return <Chip label="No membership" size="small" />;
    }

    const colors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
      active: 'success',
      trialing: 'warning',
      expired: 'error',
      cancelled: 'error',
    };

    return (
      <Chip
        label={user.membership_status}
        size="small"
        color={colors[user.membership_status] || 'default'}
      />
    );
  };

  const getFunnelStepChip = (user: AdminUser) => {
    // Only show funnel step for pending users (it's irrelevant for active/trialing users)
    if (user.membership_status !== 'pending') {
      return '-';
    }

    if (!user.funnel_step) {
      return <Chip label="App only" size="small" variant="outlined" />;
    }

    const labels: Record<string, string> = {
      paywall_viewed: 'Saw paywall',
      checkout_started: 'Started checkout',
    };

    const colors: Record<string, 'info' | 'warning'> = {
      paywall_viewed: 'info',
      checkout_started: 'warning',
    };

    return (
      <Chip
        label={labels[user.funnel_step] || user.funnel_step}
        size="small"
        color={colors[user.funnel_step] || 'default'}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Users
      </Typography>

      {/* Search + filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search by email or user ID..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            sx={{ minWidth: 320, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Membership Status</InputLabel>
            <Select
              value={statusFilter}
              label="Membership Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Activity</InputLabel>
            <Select
              value={activityFilter}
              label="Activity"
              onChange={(e) => {
                setActivityFilter(e.target.value);
                setPage(0);
              }}
            >
              {ACTIVITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell sortDirection={sortBy === 'email' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>Role</TableCell>
              <TableCell sortDirection={sortBy === 'membership_status' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'membership_status'}
                  direction={sortBy === 'membership_status' ? sortOrder : 'asc'}
                  onClick={() => handleSort('membership_status')}
                >
                  Membership
                </TableSortLabel>
              </TableCell>
              <TableCell>Funnel Step</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Auto Renew</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell sortDirection={sortBy === 'last_active_at' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'last_active_at'}
                  direction={sortBy === 'last_active_at' ? sortOrder : 'desc'}
                  onClick={() => handleSort('last_active_at')}
                >
                  Last Active
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'created_at' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortOrder : 'desc'}
                  onClick={() => handleSort('created_at')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.user_id}
                  hover
                  onClick={() => navigate(`/admin/users/${user.user_id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{user.user_id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      color={user.role === 'admin' || user.role === 'super-admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{getMembershipChip(user)}</TableCell>
                  <TableCell>{getFunnelStepChip(user)}</TableCell>
                  <TableCell>{formatDate(user.membership_end_date)}</TableCell>
                  <TableCell>
                    {user.auto_renew !== null && (
                      <Chip
                        label={user.auto_renew ? 'Yes' : 'No'}
                        size="small"
                        color={user.auto_renew ? 'success' : 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.profile_complete ? 'Complete' : 'Incomplete'}
                      size="small"
                      color={user.profile_complete ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.last_active_at)}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default UserList;
